import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../../../utils/store';
import { useEffect, useState } from 'react';
import HttpService from '../../../services/HttpService';
import Constants from '../../../utils/constants';
import { Stack } from '@mui/material';
import FormFieldData from '../../../Models/FormFieldData';
import CommonForm from '../../../components/form/CommonForm';
import AlertMessageComponent from '../../../components/AlertMessageComponent';
import React from 'react';
import { useAlert } from '../../../components/alertContext';
import { handleErrorMessage } from '../../../utils/errorsUtil';

enum InductionStatus {
  NotApplicable = '0',
  Yes = '1',
  No = '2',
}
const InductionList = [
  {
    id: InductionStatus.Yes,
    label: 'Yes',
  },
  { id: InductionStatus.No, label: 'No' },
  {
    id: InductionStatus.NotApplicable,
    label: 'N/A',
  },
];
const ContractorForm: React.FC = () => {
  const { getAdmin } = useStore();
  const { currentId } = useParams<{ currentId: any }>();
  const navigate = useNavigate();

  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);

  type ContractorFormDictionary = {
    [k: string]: { required: boolean; value: any };
  };
  const [contractorForm, setContractorForm] =
    useState<ContractorFormDictionary>({
      name: { required: true, value: '' },
      id: { required: false, value: null },
      email: { required: true, value: '' },
      address: { required: false, value: '' },
      phone: { required: true, value: '' },
      creditLimit: { required: false, value: '' },
      notes: { required: false, value: '' },
      completed: { required: false, value: InductionStatus.NotApplicable },
      abn: { required: true, value: '' },
    });

  useEffect(() => {
    if (currentId) {
      if (currentId !== 'Add') {
        fetchSingleRecord(currentId);
      }
    } else {
      setCurrentRecord(null);
    }
  }, [currentId]);

  const fetchSingleRecord = async (id: any) => {
    try {
      const response = await HttpService.get(
        Constants.apiURL + Constants.contractorsURL + '/' + id,
        { headers: { Authorization: 'Bearer ' + getAdmin() } },
        null
      );
      if (response.success) {
        handleRowClick(response.data);
      }

      console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`Error fetching data:`, error);
      setAlert({
        type: 'error',
        message: handleErrorMessage(error),
        open: true,
      });
    }
  };

  const handleContractorFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setContractorForm((prev) => {
      return {
        ...prev,
        [name]: { required: prev[name].required, value: value },
      };
    });
  };

  const clearForm = () => {
    setContractorForm({
      name: { required: true, value: '' },
      id: { required: false, value: null },
      email: { required: true, value: '' },
      address: { required: false, value: '' },
      phone: { required: true, value: '' },
      creditLimit: { required: false, value: '' },
      notes: { required: false, value: '' },
      completed: { required: false, value: InductionStatus.NotApplicable },
      abn: { required: true, value: '' },
    });
    setAlert({
      type: 'error',
      message: '',
      open: false,
    });
    setCurrentRecord(null);
    navigate('/data-management/contractor');
  };

  const handleSave = async () => {
    interface ContractorRequest {
      name: string;
      id: string;
      email: string;
      address: string;
      phone: string;
      creditLimit: string;
      notes: string;
      completed:
        | string
        | InductionStatus.No
        | InductionStatus.Yes
        | InductionStatus.NotApplicable;
      abn: string;
    }
    const contractorReq: ContractorRequest = {
      name: contractorForm.name.value,
      id: contractorForm.id.value,
      email: contractorForm.email.value,
      address: contractorForm.address.value,
      phone: contractorForm.phone.value,

      creditLimit: contractorForm.creditLimit.value,
      notes: contractorForm.notes.value,
      completed: contractorForm.completed.value,
      abn: contractorForm.abn.value,
    };

    setLoading(true);
    const endpoint = Constants.apiURL + Constants.contractorsURL;
    if (currentRecord) {
      contractorReq.id = currentRecord.id;
    }
    let response: any;
    try {
      if (currentRecord) {
        response = await HttpService.put(endpoint, contractorReq, {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        });
      } else {
        response = await HttpService.post(endpoint, contractorReq, {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        });
      }
      if (response.success) {
        clearForm();
      }
    } catch (error: any) {
      console.error(`Error fetching data:`, error);
      setAlert({
        type: 'error',
        message: handleErrorMessage(error),
        open: true,
      });
    } finally {
      setLoading(false);
    }
    console.log(response);
  };

  const handleRowClick = (record: any) => {
    setCurrentRecord(record);
    setContractorForm({
      name: { required: true, value: record.name },
      id: { required: false, value: record.id },
      email: { required: true, value: record.email },
      address: { required: false, value: record.address },
      phone: { required: true, value: record.phone },
      contractorID: { required: true, value: record.contractorID },
      creditLimit: { required: false, value: record.creditLimit },
      notes: { required: false, value: record.notes },
      completed: { required: false, value: record.completed },
      abn: { required: true, value: record.abn },
    });
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex =
    /^(\+?\(61\)|\(\+?61\)|\+?61|\(0[1-9]\)|0[1-9])?( ?-?[0-9]){7,9}$/; // Validates a 10-digit phone number

  const formFields: FormFieldData[] = [
    {
      name: 'name',
      label: 'Contractor / Supplier',
      value: contractorForm.name.value,
      onChange: handleContractorFormChange,
      isRequired: contractorForm.name.required,
      size: 6,
      type: 'textfield',
      validationRules: [
        (value) => (!value ? 'Contractor name is required' : ''),
      ],
    },
    {
      name: 'abn',
      label: 'ABN Number',
      value: contractorForm.abn.value,
      onChange: handleContractorFormChange,
      isRequired: contractorForm.abn.required,
      size: 6,
      type: 'textfield',
      inputType: 'number',
      validationRules: [
        (value) => (!value ? 'ABN is required' : ''),
        (value) =>
          value.length !== 11 ? 'Invalid ABN. Must be 11 digits' : '',
      ],
    },
    {
      name: 'address',
      label: 'Address',
      value: contractorForm.address.value,
      onChange: handleContractorFormChange,
      isRequired: contractorForm.address.required,
      size: 12,
      type: 'textfield',
      validationRules: [],
    },
    {
      name: 'email',
      label: 'Email Address',
      value: contractorForm.email.value,
      onChange: handleContractorFormChange,
      isRequired: contractorForm.email.required,
      size: 6,
      type: 'textfield',
      inputType: 'email',
      validationRules: [
        (value) => (!value ? 'Email address is required' : ''),
        (value) => (!emailRegex.test(value) ? 'Invalid email address' : ''),
      ],
    },
    {
      name: 'phone',
      label: 'Phone Number',
      value: contractorForm.phone.value,
      onChange: handleContractorFormChange,
      isRequired: contractorForm.phone.required,
      size: 6,
      type: 'textfield',
      inputType: 'tel',
      validationRules: [
        (value) => (!value ? 'Phone number is required' : ''),
        (value) =>
          !phoneRegex.test(value) ? 'Invalid Australian phone number' : '',
      ],
    },
    {
      name: 'creditLimit',
      label: 'Credit Limit',
      value: contractorForm.creditLimit.value,
      onChange: handleContractorFormChange,
      isRequired: contractorForm.creditLimit.required,
      size: 6,
      type: 'textfield',
      inputType: 'number',
      validationRules: [],
    },
    {
      name: 'completed',
      label: 'Loumac Induction Completed',
      value: contractorForm.completed.value,
      onChange: handleContractorFormChange,
      isRequired: contractorForm.completed.required,
      size: 12,
      type: 'multicheck',
      validationRules: [],
      options: InductionList,
    },
    {
      name: 'notes',
      label: 'Notes',
      value: contractorForm.notes.value,
      onChange: handleContractorFormChange,
      isRequired: contractorForm.notes.required,
      size: 12,
      type: 'textareaField',
      validationRules: [],
      minRows: 7,
      keyname: 'txtareanotes',
    },
  ];
  return (
    <Stack>
      <CommonForm
        fields={formFields}
        onCancel={() => {}}
        formReset={clearForm}
        formTitle={
          currentId !== 'Add' ? 'Update Contractor' : 'Add New Contractor'
        }
        isLoading={loading}
        isUpdate={currentId !== 'Add' ? true : false}
        onSave={handleSave}
        actionButtonPosition="bottom"
      />
      <AlertMessageComponent
        type={alert.type}
        open={alert.open}
        handleClose={handleCloseAlert}
        message={alert.message}
      />
    </Stack>
  );
};

export default ContractorForm;
